import React from "react";
import {  ITooltipParams, ICellRendererParams } from "ag-grid-community";
import { ColumnType, DataTableOption, FilterParamType, FilterType } from "../DataTable/DataTypes";
import { LoaderType, MyColDef, View } from "./shared/interface";
import CheckboxHeader from "./components/HeaderCheckbox";
import HeaderIcon, { MyCustomInnerHeaderProps } from "./components/HeaderIcon";
import Loader from "./components/Loader";
import CustomCellRenderer from "./components/CustomCellRenderer";
import CustomTextboxFloatingFilter from "./components/CustomTextboxFloatingFilter";
import CustomDateRangeFloatingFilter from "./components/CustomDateRangeFloatingFilter";
import CustomDropdownFloatingFilter from "./components/CustomDropdownFloatingFilter";
import ActionButtonRenderer from "./components/ActionButtons";
import { useDataTableContext } from "./shared/Context";
import CheckboxRenderer from "./components/CheckboxRenderer";
import CustomBooleanToggleFloatingFilter from "./components/CustomBooleanToggleFloatingFilter";

export function mapToAGGridColumns<RowType>(dataTableOption: DataTableOption, name?: string, VIEW_FIELDS? : Record<View, Set<string>>, urlSearch: string = ""): MyColDef<RowType>[] {
  const { TableView } = useDataTableContext();
    const urlParams = new URLSearchParams(urlSearch);

    const DEBUG_MODE = false;

    const defaultDetailAction = dataTableOption?.columns?.find(col => col.detailPageNav)?.detailPageNav;

    return dataTableOption?.columns?.map(col => {
      // override defaultFilterValue if urlSearch exists
      if (col.filterField) {
        const queryValue = urlParams.get(col.filterField);
        if (queryValue !== null) {
          // replace defaultFilterValue with url param
          col.defaultFilterValue = [queryValue];
        }
      }
      const { floatingFilterComponent, floatingFilterComponentParams } = getFloatingFilterComponentWithParams(col, name);
      const {tooltipValueGetter, tooltipComponent} = getTooltipComponent(col);
      const detailAction = col?.customNavigation ?? defaultDetailAction;

      const colDef: MyColDef<RowType> = {
          field: col?.key,
          headerName: typeof col.title === 'string' ? col.title : undefined,
          headerComponent: getHeaderComponent(col.type, col.title), 
          headerComponentParams : getHeaderComponentParams({ type: col.type, title : col.title}),
          cellRenderer:  (row : ICellRendererParams) => getCellRendererComponent(col,row,dataTableOption),
          floatingFilterComponent,
          floatingFilterComponentParams, 
          hide: getColumnVisibility(col, TableView, VIEW_FIELDS ?? {} as Record<View, Set<string>>),
          sortable: col.sortable ?? false,
          sort: dataTableOption?.defaultSortField === col?.sortKey ? dataTableOption?.defaultSortOrder : undefined,
          sortingParam: col.sortKey,
          searchingParam: getSearchingParam(col.filterField, col.filterParams), 
          lazyLoadParamKey: col?.lazyLoadParamKey ?? "",
          filter: col.filterable ?? false,
          filterParams: mapFilterParams(col.filterParams, col.filterType),
          width: getColumnWidths(col),
          minWidth : col.minWidth ?? undefined,
          maxWidth : col.maxWidth ?? undefined,
          flex: col.flex ?? 0,
          resizable: col.resizable ?? false,
          cellClass: `${col?.customClass ??''} ${col?.extraClass??''}`,
          headerClass: `${col?.headerClass ??''}`,
          pinned: getPinnedPosition(col.customClass),
          tooltipValueGetter,
          tooltipComponent,
          autoHeaderHeight: true,
          wrapHeaderText: true,
          backendFilterType: col.filterType && isValidFilterType(col.filterType)? col.filterType : undefined,
          ...getSpecialColumnCases(col, dataTableOption),
          ...onCellClickedEvent(col, detailAction),
          headerValueGetter: (params) => {
            if (DEBUG_MODE) {
              const width = params.column.getActualWidth();
              const min = params.column.getColDef().minWidth;
              const max = params.column.getColDef().maxWidth;
              return `${col.title}\n(${width}px / ${min}-${max})`;
            }
            return typeof col.title === 'string' ? col.title : undefined;
          }
      };
      if ((col.filterType === "select" || col.filterType === "multiselect") && !colDef.floatingFilterComponentParams) {
          colDef.floatingFilterComponentParams = {
            values: [],
          };
      }

      if (DEBUG_MODE) {
        colDef.headerValueGetter = (params) => {
            if (DEBUG_MODE) {
              const width = params.column.getActualWidth();
              const min = params.column.getColDef().minWidth;
              const max = params.column.getColDef().maxWidth;
              return `${col.title}\n(${width}px / ${min}-${max})`;
            }
            return typeof col.title === 'string' ? col.title : undefined;
          }
      }
      if(colDef.minWidth === undefined){
        colDef.minWidth = colDef.width;
      }
 
    return colDef;
    });
}

const getColumnVisibility = (col: ColumnType<any>,TableView : View, VIEW_FIELDS? : Record<View, Set<string>>) : boolean => {
  // while refreshing Table - preserveView
  const preservedView = localStorage.getItem('preservedView') ?? null;
  localStorage.removeItem('preservedView');
  if (preservedView  && VIEW_FIELDS?.hasOwnProperty(preservedView)) {
    return !VIEW_FIELDS[preservedView].has(col?.key);
  }

  return col.enabledInColumn === false;
}

const getColumnWidths = (col: ColumnType<any>) : number=> {
  if(col.width){
    return col.width;
  }
  if (col.newCellWidth && col.newCellWidth.endsWith('px')) {
    const pixelWidth = parseInt(col.newCellWidth);
    return pixelWidth;
  }
  return 40;
}

const getHeaderComponent = (type : string, title? : string | JSX.Element) => {
      if(title && React.isValidElement(title)){ 
        return HeaderIcon; 
      }
        
      switch (type) {
        case 'bulk_action_checkbox': 
            return CheckboxHeader;
        case 'action_button': 
            return HeaderIcon;
        default: 
            return undefined;
      }
}

interface IgetHeaderComponentParams {
  type: string,
  title?: string | JSX.Element,
  defaultParams?: MyCustomInnerHeaderProps,
}

const getHeaderComponentParams = (params : IgetHeaderComponentParams) : MyCustomInnerHeaderProps => {
    const { type, defaultParams, title } = params
    if(title && React.isValidElement(title)) {
      return {
        ...defaultParams,
        headerElement: title,
      };
    }

     switch (type) {
        case 'action_button': 
        return { 
            ...defaultParams,
            icon: "fa fa-ellipsis-h",
          };
        default: 
            return undefined;
    }
}

function getLoaderTypeFromColumn(col: ColumnType<any>): LoaderType {
  switch (col.key) {
    case "state":
    case "alarm":{
      return LoaderType.State;
    }
    case "image_url":{
      return LoaderType.Image;
    }
    case "last_update_tmst":{
      return LoaderType.Date;
    }
    case "action_button":{
      return LoaderType.ActionButton;
    }
    case "connection_uuid":
    case "device_profile_uuid":{
      return LoaderType.BoxShortWidth;
    }
    case "deviceUuid":
    case "deviceId":
    case "name":
    case "firmware_version":
    case "description":{
      return LoaderType.BoxFullWidth;
    }
    case "operationalStatus":{
      return LoaderType.Icon;
    }
  }

  switch (col.type) {
    case "icon_with_tooltip":{
      return LoaderType.Icon;
    }
    case "action_button":{
      return LoaderType.ActionButton;
    }    
    case "date":{
      return LoaderType.Date;
    }
    case "text_with_tooltip":
    case "text":{
      return LoaderType.BoxFullWidth;
    }
  }
  return undefined;
}

function getCellRendererComponent<RowType>(col: ColumnType<RowType>, row: ICellRendererParams, dataTableOption: DataTableOption) {
  const type = col.type.toString();
  const data = row.data;

  let hasValue = false;
  if (data && typeof data === "object") {
    for (const key in data) {
      const val = data[key];
      if (val !== undefined && val !== null && val !== "") {
        hasValue = true;
        break;
      }
    }
  }

  if (!hasValue) {
    if (col.type === 'bulk_action_checkbox') {
      return null;
    }
    const loaderType = getLoaderTypeFromColumn(col);
    return <Loader {...row} type={loaderType} />;
  }

  if (type === "action_button") {
    const data = row?.data as RowType;
    const serviceActions = typeof dataTableOption.addServiceActions === "function" ?dataTableOption.addServiceActions(data) : [];
    const rowIndex = (data as any)?.rowIndex ?? 0;
    return (
      <ActionButtonRenderer
        row={data}
        index={rowIndex}
        actions={dataTableOption.actions}
        isAllFieldVisible={false}
        addServiceActions={true}
        hasRowHeightOption = { dataTableOption?.hasRowHeightOption ?? false}
        mergedColumns={serviceActions.map((action, idx) => {
            if (action.visible && !action.visible(data)) return null;
            return (
              <button key={idx} onClick={() => action.action(data)} title={action.text} className="flex items-center justify-center rounded border border-gray-300 hover:bg-gray-200">
                {action.render ? action.render(data) : action.text?.charAt(0)}
              </button>
            );
          })
          .filter(Boolean)}
      />
    );
  }

  return (
    <CustomCellRenderer {...row} col={col}  enableMultilineCellRender={col?.enableMultilineCellRender} hasRowHeightOption = {dataTableOption?.hasRowHeightOption ?? false}/>
  );
}

function getFloatingFilterComponentWithParams(col: ColumnType<any>,name : string) {
  const { filterType, inputValidation, inputValidationHint, maxInputLength, filterParams } = col;
  let floatingFilterComponent;
  let floatingFilterComponentParams;
  switch (filterType) {
    case "text" : {
      floatingFilterComponent = CustomTextboxFloatingFilter;
      floatingFilterComponentParams = {
        inputValidation: inputValidation,
        inputValidationHint: inputValidationHint,
        maxInputLength: maxInputLength,
        name,
        defaultFilterValue: col.defaultFilterValue ?? [],
        filterParams: col?.filterParams,
      };
      break;
    }
case "select":
case "multiselect": {
  floatingFilterComponent = CustomDropdownFloatingFilter;

  const defaultFilterValue = col.defaultFilterValue ?? [];

  let values: any[] | (() => Promise<any[]>) = [];

  try {
    if (col.filterParams?.optionFetcher) {
      values = async () => {
        const rawOptions = await col.filterParams.optionFetcher();
        return col.filterParams.mapper ? rawOptions.map(col.filterParams.mapper) : rawOptions;
      };
    } else {
      values = col.filterParams?.options ?? [];
    }
  } catch (error) {
    console.error("Error loading dropdown options:", error);
  }

  floatingFilterComponentParams = {
    defaultFilterValue,
    values,
    name
  };

  break;
}

    case "date":
    case "daterange":{
      floatingFilterComponent = CustomDateRangeFloatingFilter;
      floatingFilterComponentParams = {
        startField: filterParams?.startField,
        endField: filterParams?.endField,
        name,
        column: col,
      };
      break;
    }

    case "boolean_toggle":{
      const defaultFilterValue = col.defaultFilterValue ?? [];
      floatingFilterComponent = CustomBooleanToggleFloatingFilter;
      floatingFilterComponentParams = {
        defaultFilterValue,
        name
      };
      break;
    }
    default:{
      return {};
    }
  }
  return {
    floatingFilterComponent,
    floatingFilterComponentParams,
  };
}

const getSearchingParam = (filterField: string, filterParams: FilterParamType) : string[] | undefined => {
  if (!filterField) return undefined;
  switch (filterField) {
    case 'date':
      return [filterParams.startField, filterParams.endField];
    default:
      return [filterField];
  }
} 

function isValidFilterType(type: string): type is "text" | "select" | "multiselect" | "daterange" {
    return ["text", "select", "multiselect", "daterange"].includes(type);
}

function mapFilterParams(filterParams?: FilterParamType, filterType?: FilterType): any {
    if (!filterParams) return undefined;
    const params: any = {};
    if (filterType === 'daterange') {
        params.startField = filterParams.startField;
        params.endField = filterParams.endField;
    }
    return Object.keys(params).length > 0 ? params : undefined;
}


function getPinnedPosition(customClass?: string): 'left' | 'right' | undefined {
  return undefined;
    if (!customClass) return undefined;
    if (customClass.includes('left-first') || customClass.includes('left-second') || customClass.includes('left')) return 'left';
    if (customClass.includes('right')) return 'right';
    return undefined;
}

const getTooltipComponent = (col: ColumnType<any>) => {
  let tooltipValueGetter, tooltipComponent;
  if (col.render_overlay_tooltip) {
    tooltipValueGetter = (p: ITooltipParams) => p.value ?? ``;
    tooltipComponent = (p: ITooltipParams) => col.render_overlay_tooltip(p.data);
  }

  if (col.render_tooltip && col.hideDefaultTooltip) {
    // const tooltipStyle: React.CSSProperties = {
    //   background: 'white',
    //   padding: '6px 10px',
    //   borderRadius: '6px',
    //   boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    //   fontSize: '12px',
    //   maxWidth: '300px',
    //   whiteSpace: 'normal',
    //   wordBreak: 'break-word',
    //   overflowWrap: 'break-word',
    //   fontFamily:'sans-serif',
    //   color: '#333',
    //   zIndex: 10000,
    // };

    tooltipValueGetter = (p: ITooltipParams) => p.value ?? ``;
    tooltipComponent = (p: ITooltipParams) => {
        const htmlString = col.render_tooltip?.(p.data as any) ?? "";
        // if htmlString is null, undefined, or empty string → don't render tooltip
        if (!htmlString) {
          return null;
        }
        return(
              <div className="custom-tooltip">
                <div
                  className="tooltip-content"
                  dangerouslySetInnerHTML={{ __html: htmlString }}
                />
              </div>
        )
    };
  }

  return {
    tooltipValueGetter,
    tooltipComponent
  };
};


function getSpecialColumnCases(col: ColumnType<any>, dataTableOption: DataTableOption): Partial<MyColDef> {
    const specialCases: Partial<MyColDef> = {};    
    if (col.type === 'bulk_action_checkbox') {
        specialCases.headerComponentParams = { primaryKey : dataTableOption?.available_key, dataTableOption : dataTableOption };
        specialCases.headerComponent = CheckboxHeader;
        specialCases.cellRenderer = CheckboxRenderer,
        specialCases.cellRendererParams = { primaryKey : dataTableOption?.available_key, hasRowHeightOption: dataTableOption?.hasRowHeightOption ?? false };
        specialCases.suppressMovable = true;
    }
    
    if (col.type === 'action_button') {
        specialCases.suppressMovable = true;
    }

  return specialCases;
}
function onCellClickedEvent(col: ColumnType<any>, detailAction?: (data: any) => void): Partial<MyColDef> {
    if(col.type === 'action_button' || col.type === 'bulk_action_checkbox' || col.detailLink === false || typeof detailAction !== 'function') {
        return {};
    }

    const clickableCell: Partial<MyColDef> = {};    
    
    clickableCell.onCellClicked = (params) => {
        const event = params.event as MouseEvent;
        const target = event.target as HTMLElement;
        if (target.closest('.no-click-propagation')) {
          return;
        }
        detailAction(params.data); 
    } ;
    
    clickableCell.cellStyle = { 
      ...clickableCell.cellStyle,
      cursor: 'pointer',
      color: '#0066cc',
    };
    
  return clickableCell;
}